import {
  calculateCommentCount,
  calculateLikeCount,
  calculateWorkoutStats,
} from 'components/WorkoutCell/utils';
import dayjs from 'dayjs';
import {
  indexSetObjectsForUI,
  setObjectToUIFacingSetIndicator,
  UserProfile,
  Workout,
  WorkoutExercise,
} from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import { stores } from 'state/stores';
import API from 'utils/API';
import { ExerciseProps } from './components/types';
import { calculateSecondColumnHeader, calculateSetValue } from './utils';
import { userExerciseWeightUnit } from 'utils/units';
import router from 'next/router';
import toast from 'react-hot-toast';
import { StatProps } from 'components/WorkoutCell/types';
import { IBAI_USERNAME } from 'utils/vip';

interface LoadingState {
  type: 'loading';
}

interface LoadedState {
  type: 'loaded';
  data: DisplayWorkoutData;
  showExerciseDetails: boolean;
}

interface ErrorState {
  type: 'error';
}

export type WorkoutDetailState = LoadingState | LoadedState | ErrorState;

type DisplayUserData = {
  username: string;
  profilePic?: string;
  description?: string;
  workoutCount: string;
  followerCount: string;
  followingCount: string;
  showFollowButton: boolean;
  isCoached: boolean;
};

type DisplayWorkoutData = {
  id: string;
  date: string;
  isPrivate: boolean;
  title: string;
  description?: string;
  isLiked: boolean;
  likeCount: string;
  commentCount: string;
  likeImages: string[];
  stats: StatProps[];
  onLikeWorkout: () => void;
  exercises: ExerciseProps[];
};

interface WorkoutDetailInterface {
  userData: DisplayUserData;
  viewState: WorkoutDetailState;
}

export class WorkoutDetailViewModel implements WorkoutDetailInterface {
  workoutId?: string;
  private _isFetchingWorkout: boolean = true;
  user?: UserProfile;

  constructor() {
    makeAutoObservable(this);
  }

  private getWorkoutByShortId = (shortId: string) => {
    const workout = Object.values(stores.workouts.workouts).find(w => w.short_id === shortId);
    return workout;
  };

  private resolveWorkoutId = (workoutId: string): string | null => {
    // Check if the ID is already in the cache as a full ID
    if (stores.workouts.workouts[workoutId]) {
      return workoutId;
    }

    // Check if it's a short ID and resolve to the full ID
    const workout = this.getWorkoutByShortId(workoutId);
    if (workout) {
      return workout.id;
    }

    return null;
  };

  /**
   * fetches the workout using the url.
   * If the urlWorkoutId param is a short Id, it is resolved to the long Id
   *
   * @param urlWorkoutId
   */
  fetchWorkout = async (urlWorkoutId: string) => {
    if (!urlWorkoutId || stores.workouts.workouts[urlWorkoutId]) return null;

    this._isFetchingWorkout = true;
    try {
      await stores.workouts.fetchWorkout(urlWorkoutId);
      // After the fetch, we need to make sure to use the long id for this.workoutId
      const resolvedWorkoutId = this.resolveWorkoutId(urlWorkoutId);
      if (!resolvedWorkoutId) throw new Error(`couldn't get full workout id`);
      this.workoutId = resolvedWorkoutId;
      this.fetchUser();
    } catch (e) {
      toast.error('Error loading the workout details');
    } finally {
      this._isFetchingWorkout = false;
    }
  };

  private fetchUser = async () => {
    if (!typeof window || !this.workoutId) return;
    const workout = stores.workouts.workouts[this.workoutId];

    if (!workout) return;

    const result = await API.getUserProfile(workout.username);

    this.user = result.data;
  };

  get userData(): DisplayUserData {
    return {
      username: this.user?.username === undefined ? `-` : `${this.user.username}`,
      profilePic: this.user?.profile_pic,
      description: this.user?.description,
      workoutCount: this.user?.workout_count === undefined ? `-` : `${this.user.workout_count}`,
      followerCount: this.user?.workout_count === undefined ? `-` : `${this.user.follower_count}`,
      followingCount: this.user?.workout_count === undefined ? `-` : `${this.user.following_count}`,
      showFollowButton: this.user?.username !== stores.account.username,
      isCoached: stores.account.is_coached,
    };
  }

  get viewState(): WorkoutDetailState {
    if (this._isFetchingWorkout) {
      return { type: 'loading' };
    }

    if (this.workoutId && stores.workouts.workouts[this.workoutId]) {
      return {
        type: 'loaded',
        data: this.formatWorkoutData(stores.workouts.workouts[this.workoutId]),
        showExerciseDetails:
          this.user?.username !== IBAI_USERNAME || this.user?.username === stores.account.username,
      };
    }

    return { type: 'error' };
  }

  private formatWorkoutData(workout: Workout): DisplayWorkoutData {
    return {
      id: workout.id,
      date: dayjs.unix(workout.end_time).fromNow(),
      isPrivate: workout.is_private,
      title: workout.name,
      description: workout.description,
      isLiked: workout.is_liked_by_user,
      likeCount: calculateLikeCount(workout),
      commentCount: calculateCommentCount(workout),
      likeImages: workout.like_images || [],
      stats: calculateWorkoutStats(workout, stores.userPreferences),
      onLikeWorkout: () => this.onLikeWorkout(workout.id),
      exercises: this.getExercises(workout.exercises),
    };
  }

  private getExercises(exercises: WorkoutExercise[]): ExerciseProps[] {
    return exercises.map(e => {
      const indexedSets = indexSetObjectsForUI(e.sets);

      return {
        key: e.id,
        image: e.thumbnail_url ?? undefined,
        title: e.title,
        supersetId: e.superset_id ?? undefined,
        notes: e.notes,
        secondColumnHeader: calculateSecondColumnHeader(e),
        restTimeSeconds: e.rest_seconds ?? undefined,
        sets: e.sets.map((s, setIndex) => ({
          key: `${s.id}`,
          indicator: setObjectToUIFacingSetIndicator(indexedSets[setIndex]),
          value: calculateSetValue(e, s, {
            weightUnit: userExerciseWeightUnit(e.exercise_template_id),
            distanceUnitShort: stores.userPreferences.distanceUnitShort,
          }),
          reps: s.reps,
          rpe: s.rpe,
          prs: s.prs,
        })),
        onExerciseClick: () => {
          router.push(`/exercise/${e.exercise_template_id}`);
        },
      };
    });
  }

  private onLikeWorkout = async (workoutId: string) => {
    await stores.workouts.likeWorkout(workoutId);
    await stores.workouts.fetchWorkout(workoutId);
  };
}
